/**
 * The per-row actions, folded behind one hamburger button — the same control
 * `bimgo-ai-web`'s `ProjectsList` renders, down to the glyph (lucide `Menu`),
 * the item order (Edit, Members, Delete) and the red delete.
 *
 * A row of bare icons competes with the row itself for attention, and the row is
 * now the primary control (clicking it opens the project's chat). One quiet
 * button that opens a menu keeps the actions available without making every row
 * look like a toolbar.
 *
 * Everything here stops propagation: the row navigates, and reaching for
 * "Delete" must never also open the project.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";

/** lucide's `Menu` — the same glyph `bimgo-ai-web`'s row trigger uses. */
function MenuIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

export interface ProjectAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
  /** Renders in red. Ordered by the caller — `bimgo-ai-web` puts it last. */
  danger?: boolean;
}

export function ProjectActionsMenu({ actions }: { actions: ProjectAction[] }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);

  /**
   * Positioned `fixed` against the viewport, not absolutely against the row.
   *
   * The table scrolls horizontally (`.members-scroll` has `overflow-x: auto`),
   * and an overflow container clips absolutely-positioned descendants — the menu
   * came out sliced off at the card's edge. `fixed` escapes every clipping
   * ancestor, at the cost of having to place it by hand.
   */
  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setAt({ top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // A fixed menu does not travel with its row, so close rather than let it
    // hang detached when anything moves underneath it.
    const onMove = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open]);

  if (actions.length === 0) return null;

  const item = (action: ProjectAction) => (
    <button
      key={action.key}
      type="button"
      role="menuitem"
      className={`row-menu-item${action.danger ? " is-danger" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(false);
        action.onSelect();
      }}
    >
      <span className="row-menu-icon">{action.icon}</span>
      {action.label}
    </button>
  );

  return (
    <div
      className="row-menu"
      ref={ref}
      // The row underneath is a navigation control; nothing in here should reach it.
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        className="icon-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("projects.columnActions")}
        title={t("projects.columnActions")}
        onClick={(e) => {
          e.stopPropagation();
          if (!open) place();
          setOpen((v) => !v);
        }}
      >
        <MenuIcon />
      </button>
      {open && at ? (
        <div
          className="row-menu-dropdown"
          role="menu"
          style={{ top: `${at.top}px`, right: `${at.right}px` }}
        >
          {actions.map(item)}
        </div>
      ) : null}
    </div>
  );
}
